import { useState, useRef, useEffect, useCallback } from 'react';
import { usePlanningStore } from '@/stores/planning';

export function ProjectSearchInput() {
  const projectFilter = usePlanningStore((s) => s.projectFilter);
  const setProjectFilter = usePlanningStore((s) => s.setProjectFilter);
  const setProjectIdFilter = usePlanningStore((s) => s.setProjectIdFilter);

  const [value, setValue] = useState(projectFilter);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mirror an externally-set filter into the input (e.g. the "Bekijk in Planning"
  // deeplink seeds store.projectFilter). During typing this is a no-op: the store
  // only updates 300ms after the last keystroke, so it never clobbers live input.
  useEffect(() => {
    setValue(projectFilter);
  }, [projectFilter]);

  const handleChange = useCallback((input: string) => {
    setValue(input);
    setProjectIdFilter(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setProjectFilter(input);
    }, 300);
  }, [setProjectFilter, setProjectIdFilter]);

  const clear = useCallback(() => {
    setValue('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setProjectFilter('');
    setProjectIdFilter(null);
  }, [setProjectFilter, setProjectIdFilter]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const borderClass = projectFilter.length >= 2
    ? 'border-td-blue ring-1 ring-td-blue'
    : 'border-gray-300';

  return (
    <div className="relative">
      <label htmlFor="pl-project-search" className="sr-only">Zoek project</label>
      <svg
        className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input
        id="pl-project-search"
        type="text"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') clear(); }}
        placeholder="Zoek project..."
        className={`w-[200px] max-lg:w-full pl-8 pr-7 py-1.5 text-xs border rounded-lg transition-colors ${borderClass}`}
      />
      {value && (
        <button
          type="button"
          onClick={clear}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Zoekopdracht wissen"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}
